import { useEffect, useState, useCallback, useRef } from 'react';
import { useBadge } from './useBadge';

/**
 * Custom hook for managing browser notifications
 * 
 * Features:
 * - Request notification permission on first use
 * - Show browser notifications when app is not focused
 * - Track notification permission state
 */

export type NotificationPermission = 'default' | 'granted' | 'denied';

interface UseNotificationsReturn {
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (title: string, options?: NotificationOptions) => void;
  isSupported: boolean;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const isSupported = 'Notification' in window;

  // Initialize permission state
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission as NotificationPermission);
    }
    // No cleanup needed - synchronous operation
  }, [isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      console.warn('Notifications are not supported in this browser');
      return 'denied';
    }

    if (permission === 'granted') {
      return 'granted';
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result as NotificationPermission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }, [isSupported, permission]);

  // Show a notification
  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!isSupported) {
      console.warn('Notifications are not supported in this browser');
      return;
    }

    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    // Only show notification if app is not focused
    if (document.hasFocus()) {
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options,
      });

      // Auto-close notification after 5 seconds
      const timeoutId = setTimeout(() => {
        notification.close();
      }, 5000);

      // Focus app when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
        clearTimeout(timeoutId);
      };

      // Clean up timeout if notification is closed manually
      notification.onclose = () => {
        clearTimeout(timeoutId);
      };
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, [isSupported, permission]);

  return {
    permission,
    requestPermission,
    showNotification,
    isSupported,
  };
};

/**
 * Hook for tracking email count changes and showing notifications
 */
interface UseEmailNotificationsOptions {
  enabled?: boolean;
  checkInterval?: number; // milliseconds
}

export const useEmailNotifications = (
  getCurrentCount: () => Promise<number>,
  options: UseEmailNotificationsOptions = {}
) => {
  const { enabled = true, checkInterval = 60000 } = options;
  const { permission, requestPermission, showNotification, isSupported } = useNotifications();
  const { setBadge, requestPermission: requestBadgePermission, isSupported: isBadgeSupported } = useBadge();
  const previousCountRef = useRef<number | null>(null);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);
  const [hasRequestedBadgePermission, setHasRequestedBadgePermission] = useState(false);

  // Request notification permission on first load (only once)
  useEffect(() => {
    let isMounted = true;

    if (enabled && isSupported && permission === 'default' && !hasRequestedPermission) {
      setHasRequestedPermission(true);
      requestPermission().catch((error) => {
        if (isMounted) {
          console.error('Error requesting notification permission:', error);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [enabled, isSupported, permission, hasRequestedPermission, requestPermission]);

  // Request badge permission on first load (only once)
  useEffect(() => {
    let isMounted = true;

    if (enabled && isBadgeSupported && !hasRequestedBadgePermission) {
      setHasRequestedBadgePermission(true);
      requestBadgePermission().catch((error) => {
        if (isMounted) {
          console.error('Error requesting badge permission:', error);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [enabled, isBadgeSupported, hasRequestedBadgePermission, requestBadgePermission]);

  // Check for new emails periodically and update badge
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const abortController = new AbortController();
    let isMounted = true;

    const checkForNewEmails = async () => {
      if (abortController.signal.aborted) {
        return;
      }

      try {
        const currentCount = await getCurrentCount();

        // Check if component is still mounted and not aborted
        if (!isMounted || abortController.signal.aborted) {
          return;
        }

        // Update iOS/Android app icon badge with unread count
        if (isBadgeSupported) {
          setBadge(currentCount);
        }

        // If we have a previous count and it increased, show notification
        if (permission === 'granted' && previousCountRef.current !== null && currentCount > previousCountRef.current) {
          const newEmailCount = currentCount - previousCountRef.current;
          showNotification('New Email in LifeUnison', {
            body: `You have ${newEmailCount} new unread email${newEmailCount > 1 ? 's' : ''}`,
            tag: 'new-emails', // Replace previous notification with same tag
          });
        }

        previousCountRef.current = currentCount;
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error checking for new emails:', error);
        }
      }
    };

    // Check immediately
    checkForNewEmails();

    // Then check periodically
    const interval = setInterval(checkForNewEmails, checkInterval);

    return () => {
      isMounted = false;
      abortController.abort();
      clearInterval(interval);
    };
  }, [enabled, permission, showNotification, getCurrentCount, checkInterval, isBadgeSupported, setBadge]);

  return {
    permission,
    requestPermission,
    isSupported,
  };
};

